import { useContext } from "react";
import { trpc } from "../../../trpc";
import MerchantContext from "../../../contexts/MerchantContext";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";

type Step2SelectWhatsappConnectionProps = {
  selectedWhatsappConnectionId: string;
  onSelect: (whatsappConnectionId: string) => void;
};

const Step2SelectWhatsappConnection = ({
  selectedWhatsappConnectionId,
  onSelect,
}: Step2SelectWhatsappConnectionProps) => {
  const { merchantId } = useContext(MerchantContext);

  const { data: connections, isLoading } = trpc.whatsappConnections.list.useQuery(
    { merchantId: merchantId || "" },
    { enabled: !!merchantId }
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex flex-col items-center gap-4">
          <Spinner size="xl" />
          <p className="text-gray-600">Cargando conexiones de WhatsApp...</p>
        </div>
      </div>
    );
  }

  if (!connections || connections.length === 0) {
    return (
      <Card>
        <CardContent className="p-6">
          <p className="py-8 text-center text-gray-500">
            No hay conexiones de WhatsApp disponibles. Por favor configura una conexión primero.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="mb-2 text-lg font-bold">Selecciona una Conexión de WhatsApp</p>
        <p className="mb-4 text-sm text-gray-600">
          Elige la conexión de WhatsApp desde la cual quieres enviar los mensajes.
        </p>
      </div>

      <RadioGroup value={selectedWhatsappConnectionId} onValueChange={onSelect}>
        <div className="flex flex-col gap-3">
          {connections.map((connection) => (
            <Card
              key={connection.id}
              className={cn(
                "cursor-pointer border-2 transition-all hover:border-purple-300 hover:bg-purple-50/50",
                selectedWhatsappConnectionId === connection.id
                  ? "border-purple-500 bg-purple-50"
                  : "border-gray-200"
              )}
              onClick={() => onSelect(connection.id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={connection.id} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">
                        {connection.phoneNumber || connection.phoneNumberId}
                      </p>
                      {connection.isPrimary && (
                        <Badge className="bg-green-100 text-green-800 border-green-200 text-xs" variant="outline">
                          Principal
                        </Badge>
                      )}
                    </div>
                    {connection.phoneNumber &&
                      connection.phoneNumber !== connection.phoneNumberId && (
                        <p className="mt-1 text-sm text-gray-500">
                          ID: {connection.phoneNumberId}
                        </p>
                      )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </RadioGroup>
    </div>
  );
};

export default Step2SelectWhatsappConnection;
